import { MarketingNav } from '@/components/layout/marketing-nav'
import { SiteFooter } from '@/components/layout/site-footer'
import { type PublicStatus, statusPageService } from '@/services/status-page.service'
import { useQuery } from '@tanstack/react-query'

const STATUS_LABELS: Record<PublicStatus, string> = {
  operational: 'Operational',
  degraded: 'Degraded Performance',
  partial_outage: 'Partial Outage',
  major_outage: 'Major Outage',
  maintenance: 'Maintenance',
}

function statusPillClasses(status: PublicStatus): string {
  switch (status) {
    case 'operational':
      return 'bg-emerald-100 text-emerald-700'
    case 'degraded':
      return 'bg-amber-100 text-amber-700'
    case 'partial_outage':
    case 'maintenance':
      return 'bg-orange-100 text-orange-700'
    case 'major_outage':
      return 'bg-red-100 text-red-700'
  }
}

export function StatusPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-status-page'],
    queryFn: () => statusPageService.getSnapshot(),
    refetchInterval: 60_000,
  })

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <MarketingNav variant="standalone" />
      <main className="container max-w-5xl px-4 pt-32 pb-16 space-y-10">
        <section className="space-y-3">
          <h1 className="text-4xl font-medium text-slate-900">Pelgo System Status</h1>
          <p className="text-slate-600">
            Live status for customer-facing services, incident communication, and scheduled
            maintenance updates.
          </p>
          {data && (
            <span
              className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${statusPillClasses(data.overall_status)}`}
            >
              {STATUS_LABELS[data.overall_status]}
            </span>
          )}
        </section>

        {isLoading && <p className="text-slate-600">Loading status...</p>}
        {isError && <p className="text-red-600">Unable to load status information right now.</p>}

        {data && (
          <>
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-slate-900">Components</h2>
              <div className="grid gap-3">
                {data.components.map((component) => (
                  <article
                    key={component.id}
                    className="rounded-xl border border-slate-200 p-4 flex items-start justify-between gap-4"
                  >
                    <div>
                      <h3 className="font-medium text-slate-900">{component.name}</h3>
                      {component.description && (
                        <p className="text-sm text-slate-600 mt-1">{component.description}</p>
                      )}
                      {component.status_message && (
                        <p className="text-xs text-slate-500 mt-2">{component.status_message}</p>
                      )}
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${statusPillClasses(component.status)}`}
                    >
                      {STATUS_LABELS[component.status]}
                    </span>
                  </article>
                ))}
                {data.components.length === 0 && (
                  <p className="text-sm text-slate-600">No components configured yet.</p>
                )}
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-slate-900">Scheduled Maintenance</h2>
              <div className="space-y-3">
                {data.maintenance.map((window) => (
                  <article key={window.id} className="rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-medium text-slate-900">{window.title}</h3>
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusPillClasses(window.status)}`}
                      >
                        {STATUS_LABELS[window.status]}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{window.message}</p>
                  </article>
                ))}
                {data.maintenance.length === 0 && (
                  <p className="text-sm text-slate-600">
                    No scheduled maintenance windows at this time.
                  </p>
                )}
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-slate-900">Incident History</h2>
              <div className="space-y-3">
                {data.incidents.map((incident) => (
                  <article key={incident.id} className="rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-medium text-slate-900">{incident.title}</h3>
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusPillClasses(
                          incident.state === 'resolved' ? 'operational' : 'degraded',
                        )}`}
                      >
                        {incident.state}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{incident.message}</p>
                    {incident.updates.length > 0 && (
                      <ul className="mt-3 text-sm text-slate-600 space-y-2">
                        {incident.updates.map((update) => (
                          <li key={update.id}>
                            <span className="font-medium text-slate-800">{update.state}:</span>{' '}
                            {update.message}
                          </li>
                        ))}
                      </ul>
                    )}
                  </article>
                ))}
                {data.incidents.length === 0 && (
                  <p className="text-sm text-slate-600">No incidents have been reported.</p>
                )}
              </div>
            </section>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  )
}
